import * as THREE from 'three';
import ExplosionPatterns from './ExplosionPatterns.js';
import Trail from '../entities/Trail.js';

/**
 * ParticleEngine类 - 管理所有粒子的创建、更新和渲染
 * 
 * 职责:
 * - 根据爆炸形状生成粒子
 * - 更新粒子位置、速度、颜色、大小
 * - 管理粒子生命周期
 * - 实现LOD（Level of Detail）优化
 * 
 * 验证需求: Requirements 4.1, 4.2, 4.3, 4.4, 4.5
 */
class ParticleEngine {
  constructor(scene, particlePool, physicsEngine) {
    if (!scene) {
      throw new Error('Scene is required');
    }
    if (!particlePool) {
      throw new Error('ParticlePool is required');
    }
    if (!physicsEngine) {
      throw new Error('PhysicsEngine is required');
    }

    this.scene = scene;
    this.particlePool = particlePool;
    this.physicsEngine = physicsEngine;
    
    // 粒子系统
    this.particleSystem = null;
    this.particleGeometry = null;
    this.particleMaterial = null;
    
    // 最大粒子数
    this.maxParticles = 5000;
    
    // 拖尾效果列表
    this.trails = [];
    
    // 性能设置
    this.enableTrails = true;
    this.qualityLevel = 'high'; // 'low', 'medium', 'high'
    
    // 初始化粒子系统
    this._initParticleSystem();
  }

  /**
   * 初始化粒子系统
   * @private
   */
  _initParticleSystem() {
    // 创建粒子几何体
    this.particleGeometry = new THREE.BufferGeometry();
    
    // 预分配缓冲区
    const positions = new Float32Array(this.maxParticles * 3);
    const colors = new Float32Array(this.maxParticles * 3);
    const sizes = new Float32Array(this.maxParticles);
    const opacities = new Float32Array(this.maxParticles);
    
    this.particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.particleGeometry.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));
    
    // 创建粒子材质
    this.particleMaterial = new THREE.PointsMaterial({
      size: 0.3,
      vertexColors: true,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true
    });
    
    // 创建粒子系统
    this.particleSystem = new THREE.Points(this.particleGeometry, this.particleMaterial);
    this.scene.add(this.particleSystem);
  }

  /**
   * 创建爆炸效果
   * @param {THREE.Vector3} position - 爆炸位置
   * @param {string} pattern - 爆炸形状 ('sphere', 'ring', 'willow', 'palm')
   * @param {Object} colors - 颜色配置 {inner: THREE.Color, outer: THREE.Color}
   * @param {number} count - 粒子数量
   */
  createExplosion(position, pattern, colors, count) {
    // 检查粒子数量限制
    const activeCount = this.particlePool.getActiveCount();
    if (activeCount >= this.maxParticles) {
      console.warn('Max particles reached, skipping explosion');
      return;
    }
    
    // 调整粒子数量以不超过限制
    const availableSlots = this.maxParticles - activeCount;
    const actualCount = Math.min(count, availableSlots);
    
    // 根据形状生成粒子数据
    const particleData = ExplosionPatterns.createPattern(pattern, position, actualCount, 8);
    
    // 创建粒子
    for (let i = 0; i < particleData.length; i++) {
      const data = particleData[i];
      const particle = this.particlePool.acquire();
      
      if (!particle) {
        console.warn('Failed to acquire particle from pool');
        break;
      }
      
      // 确定粒子颜色（内层和外层）
      // 内层粒子（前30%）使用内层颜色，其余使用外层颜色
      const isInner = i < particleData.length * 0.3;
      const color = isInner ? colors.inner : colors.outer;
      
      // 粒子大小变化（0.1-0.5）
      const size = 0.1 + Math.random() * 0.4;
      
      // 生命值变化（2-4秒）
      const maxLife = 2 + Math.random() * 2;
      
      // 初始化粒子
      particle.init({
        position: data.position,
        velocity: data.velocity,
        color: color,
        size: size,
        maxLife: maxLife
      });
      
      // 为大粒子添加拖尾效果
      if (this.enableTrails && particle.shouldHaveTrail()) {
        const trail = new Trail(15, color, 0.6);
        particle.trail = trail;
        this.trails.push(trail);
        this.scene.add(trail.getMesh());
      }
    }
  }

  /**
   * 更新所有粒子
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    const activeParticles = this.particlePool.activeParticles;
    const positions = this.particleGeometry.getAttribute('position');
    const colors = this.particleGeometry.getAttribute('color');
    const sizes = this.particleGeometry.getAttribute('size');
    const opacities = this.particleGeometry.getAttribute('opacity');
    
    let particleIndex = 0;
    const particlesToRelease = [];
    
    // 更新每个活跃粒子
    for (const particle of activeParticles) {
      if (!particle.active) {
        particlesToRelease.push(particle);
        continue;
      }
      
      // 更新粒子生命周期
      particle.update(deltaTime);
      
      // 应用物理效果
      this.physicsEngine.update(particle, deltaTime);
      
      // 更新拖尾
      if (particle.trail) {
        particle.trail.update(particle.position, particle.getSpeed());
      }
      
      // 检查粒子是否仍然活跃
      if (!particle.active) {
        particlesToRelease.push(particle);
        continue;
      }
      
      // 更新渲染数据
      if (particleIndex < this.maxParticles) {
        positions.setXYZ(particleIndex, particle.position.x, particle.position.y, particle.position.z);
        colors.setXYZ(particleIndex, particle.color.r, particle.color.g, particle.color.b);
        sizes.setX(particleIndex, particle.size);
        opacities.setX(particleIndex, particle.opacity);
        particleIndex++;
      }
    }
    
    // 释放非活跃粒子
    for (const particle of particlesToRelease) {
      // 清理拖尾
      if (particle.trail) {
        const trailIndex = this.trails.indexOf(particle.trail);
        if (trailIndex !== -1) {
          this.scene.remove(particle.trail.getMesh());
          particle.trail.dispose();
          this.trails.splice(trailIndex, 1);
        }
      }
      
      this.particlePool.release(particle);
    }
    
    // 更新几何体
    positions.needsUpdate = true;
    colors.needsUpdate = true;
    sizes.needsUpdate = true;
    opacities.needsUpdate = true;
    
    // 设置绘制范围
    this.particleGeometry.setDrawRange(0, particleIndex);
  }

  /**
   * 获取活跃粒子数量
   * @returns {number}
   */
  getActiveParticleCount() {
    return this.particlePool.getActiveCount();
  }

  /**
   * 设置最大粒子数
   * @param {number} max - 最大粒子数
   */
  setMaxParticles(max) {
    this.maxParticles = Math.min(max, 10000);
  }

  /**
   * 设置质量级别
   * @param {string} level - 质量级别 ('low', 'medium', 'high')
   */
  setQualityLevel(level) {
    this.qualityLevel = level;
    
    switch (level) {
      case 'low':
        this.enableTrails = false;
        this.setMaxParticles(2000);
        break;
      case 'medium':
        this.enableTrails = false;
        this.setMaxParticles(3500);
        break;
      case 'high':
        this.enableTrails = true;
        this.setMaxParticles(5000);
        break;
    }
  }

  /**
   * 清除所有粒子
   */
  clear() {
    // 清理所有拖尾
    for (const trail of this.trails) {
      this.scene.remove(trail.getMesh());
      trail.dispose();
    }
    this.trails = [];
    
    // 释放所有粒子
    this.particlePool.clear();
    
    // 重置绘制范围
    this.particleGeometry.setDrawRange(0, 0);
  }

  /**
   * 清理资源
   */
  dispose() {
    this.clear();
    
    if (this.particleGeometry) {
      this.particleGeometry.dispose();
    }
    
    if (this.particleMaterial) {
      this.particleMaterial.dispose();
    }
    
    if (this.particleSystem) {
      this.scene.remove(this.particleSystem);
    }
  }
}

export default ParticleEngine;
